import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const location = useLocation();
  const { language, toggleLanguage, getText } = useLanguage();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const navItems = [
    { path: '/', labelZh: '首頁', labelEn: 'Home' },
    { path: '/about', labelZh: '關於我們', labelEn: 'About Us' },
    { path: '/services', labelZh: '服務收費', labelEn: 'Services' },
    { path: '/employers', labelZh: '僱主須知', labelEn: 'Employers' },
    { path: '/resources', labelZh: '有用資料', labelEn: 'Resources' },
    { path: '/faq', labelZh: '常見問題', labelEn: 'FAQ' },
    { path: '/contact', labelZh: '聯絡我們', labelEn: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const defaultLogo = "https://customer-assets.emergentagent.com/job_homecarer/artifacts/91c1hhkd_image.png";

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={content?.navLogoUrl || defaultLogo}
              alt="Maidful Employment Agency Limited" 
              className="h-28 w-auto object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultLogo;
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'text-gray-700 hover:text-white'
                }`}
                style={{
                  backgroundColor: isActive(item.path) ? '#00b6a5' : 'transparent',
                  ':hover': { backgroundColor: '#00b6a5' }
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.target.style.backgroundColor = '#00b6a5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {getText(item.labelZh, item.labelEn)}
              </Link>
            ))}
          </div>

          {/* Language Switcher & Contact Button */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Language Switcher - Hidden until English content is ready */}

            <a
              href="https://api.whatsapp.com/send?phone=85261871388"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="font-medium transition-all duration-300"
                style={{
                  backgroundColor: '#c8ff00',
                  color: '#000',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#b8ef00';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#c8ff00';
                }}
              >
                <Phone className="mr-2 h-4 w-4" />
                {getText('聯絡我們', 'Contact Us')}
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Mobile Language Switcher - Hidden until English content is ready */}

            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'text-gray-700'
                }`}
                style={{
                  backgroundColor: isActive(item.path) ? '#00b6a5' : 'transparent'
                }}
              >
                {getText(item.labelZh, item.labelEn)}
              </Link>
            ))}
            <a
              href="https://api.whatsapp.com/send?phone=85261871388"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 py-2"
            >
              <Button
                className="w-full font-medium"
                style={{
                  backgroundColor: '#c8ff00',
                  color: '#000',
                }}
              >
                <Phone className="mr-2 h-4 w-4" />
                {getText('聯絡我們', 'Contact Us')}
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
